// ---------------------------------------------------------------------------
// VersionUpdateBanner — a non-blocking "a new version is available" prompt.
//
// A single-page app served from immutable, content-hashed asset files has one
// failure mode a manual refresh always fixes but an untouched tab never sees:
// after a new deploy the open tab still runs the OLD shell and lazily imports
// OLD chunk filenames. Those old chunks are kept around for in-flight tabs, but
// the tab is nonetheless a build behind and will eventually hit a route whose
// chunk no longer exists. This component closes that gap WITHOUT ever forcing a
// reload out from under the user: it polls the `version.json` manifest the
// build emits, compares the deployed build id against the one baked into THIS
// bundle, and — only on a mismatch — shows a dismissible banner with a Refresh
// button. The reload is always user-initiated.
//
// This is the JS twin of @vectros-ai/react's VersionUpdateBanner (the two
// mirrored reference apps consume the shared React-19 package; the developer
// portal is React 18 and not wired to it, so it carries its own copy). Keep the
// two behaviors in sync.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

// Build ids that mean "not a real deployed build" — never poll for these.
const NON_DEPLOY_VERSIONS = new Set(['', 'dev', 'development']);

/**
 * Polls a `version.json` manifest and, when the deployed build id differs from
 * the running one, shows a non-blocking banner offering a user-initiated
 * refresh. Renders nothing until a newer version is detected.
 */
export function VersionUpdateBanner({
  currentVersion,
  versionUrl = '/version.json',
  pollIntervalMs = 60_000,
  message = 'A new version of this app is available.',
  refreshLabel = 'Refresh',
  dismissLabel = 'Dismiss',
}) {
  // 'idle' → still polling; 'available' → newer build detected, banner shown;
  // 'dismissed' → user dismissed. Polling stops the moment we leave 'idle', so
  // a dismiss is respected (we never re-detect and re-nag on the next tick).
  const [status, setStatus] = useState('idle');

  const currentVersionRef = useRef(currentVersion);
  currentVersionRef.current = currentVersion;

  const enabled = !NON_DEPLOY_VERSIONS.has(currentVersion);

  const check = useCallback(async () => {
    try {
      const res = await fetch(versionUrl, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const deployed = data && typeof data === 'object' ? String(data.version ?? '') : '';
      if (deployed && deployed !== currentVersionRef.current) {
        setStatus('available');
      }
    } catch {
      // Ignore — poll again on the next tick / focus.
    }
  }, [versionUrl]);

  useEffect(() => {
    if (!enabled || status !== 'idle') return undefined;

    let cancelled = false;
    const runCheck = () => {
      if (!cancelled) void check();
    };

    const intervalId = setInterval(runCheck, pollIntervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') runCheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', runCheck);

    runCheck();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', runCheck);
    };
  }, [enabled, status, pollIntervalMs, check]);

  if (status !== 'available') return null;

  return (
    <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert
        severity="info"
        variant="filled"
        action={
          <>
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              {refreshLabel}
            </Button>
            <Button color="inherit" size="small" onClick={() => setStatus('dismissed')}>
              {dismissLabel}
            </Button>
          </>
        }
      >
        {message}
      </Alert>
    </Snackbar>
  );
}
